import type { DomainDetailDto } from "@bafe/api-client";
import {
	ArrowRightIcon,
	CheckCircleIcon,
	CloudIcon,
	WarningCircleIcon,
} from "@phosphor-icons/react";
import {
	Button,
	Card,
	Checkbox,
	CopyField,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Section,
	Skeleton,
	cn,
	useToast,
} from "@bafe/ui";
import { isApexHostname, validateDomain } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DnsRecordList } from "../../components/DnsRecordList";
import { ErrorPanel } from "../../components/ErrorPanel";
import { LinkButton } from "../../components/LinkButton";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";

type StepId = 1 | 2 | 3;

function purposeReady(detail: DomainDetailDto): boolean {
	if (detail.emailEnabled && detail.mailReadyAt === null) return false;
	if (detail.sitesEnabled && detail.sitesReadyAt === null) return false;
	if (detail.platformApp && detail.platformReadyAt === null) return false;
	return detail.emailEnabled || detail.sitesEnabled || detail.platformApp !== null;
}

function needsOwnershipTxt(detail: DomainDetailDto): boolean {
	return detail.emailEnabled || detail.sitesEnabled;
}

const STEPS: Array<{ id: StepId; label: string }> = [
	{ id: 1, label: "Your domain" },
	{ id: 2, label: "Prove you own it" },
	{ id: 3, label: "Connect DNS" },
];

/**
 * Background refresh cadence while the customer waits.
 *
 * Key's own worker performs the DNS lookups; this only re-reads the result,
 * so it can be frequent without touching the customer's DNS provider.
 */
const POLL_MS = 20_000;

export function DomainNew() {
	const [searchParams, setSearchParams] = useSearchParams();
	const domainId = searchParams.get("domain");

	const domain = useQuery({
		queryKey: queryKeys.domain(domainId ?? ""),
		queryFn: () => api.getDomain(domainId ?? ""),
		enabled: domainId !== null,
		refetchInterval: (query) => {
			const data = query.state.data;
			return data && !purposeReady(data) ? POLL_MS : false;
		},
	});

	const detail = domain.data ?? null;
	const step: StepId =
		detail === null ? 1
		: needsOwnershipTxt(detail) && detail.ownershipVerifiedAt === null ? 2
		: 3;
	const ready = detail !== null && purposeReady(detail);

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				title={ready ? `${detail?.domainName ?? "Your domain"} is ready.` : "Add your domain"}
				description={
					ready
						? undefined
						: "Choose what this hostname is for, then make the DNS changes Key shows you."
				}
				breadcrumb={
					<Link
						to="/manage/domains"
						className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
					>
						Domains
					</Link>
				}
			/>

			{!ready && <StepIndicator current={step} />}

			{domainId !== null && domain.isPending && <Skeleton className="h-64 w-full" label="Loading your domain" />}

			{domain.isError && (
				<ErrorPanel
					error={domain.error}
					title="We could not load this domain."
					onRetry={() => void domain.refetch()}
					retryPending={domain.isFetching}
				/>
			)}

			{detail === null && domainId === null && (
				<StepOne
					onCreated={(created) => {
						setSearchParams({ domain: created.id }, { replace: true });
					}}
				/>
			)}

			{detail !== null && ready && <ReadyState detail={detail} />}
			{detail !== null && !ready && step === 2 && <StepTwo detail={detail} />}
			{detail !== null && !ready && step === 3 && <StepThree detail={detail} />}
		</div>
	);
}

function StepIndicator({ current }: { current: StepId }) {
	return (
		<ol className="flex flex-col gap-2 sm:flex-row sm:gap-6" aria-label="Setup progress">
			{STEPS.map((step) => {
				const state = step.id < current ? "done" : step.id === current ? "current" : "todo";
				return (
					<li key={step.id} className="flex items-center gap-2">
						<span
							aria-hidden="true"
							className={cn(
								"inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
								state === "done" && "bg-(--color-positive-50) text-(--color-positive-700)",
								state === "current" && "bg-(--color-brand-600) text-white",
								state === "todo" && "bg-(--surface-sunken) text-(--text-muted)",
							)}
						>
							{state === "done" ? "✓" : step.id}
						</span>
						<span
							className={cn(
								"text-sm",
								state === "current"
									? "font-medium text-(--text-primary)"
									: "text-(--text-secondary)",
							)}
						>
							{step.label}
							{state === "current" && <span className="sr-only"> (current step)</span>}
							{state === "done" && <span className="sr-only"> (done)</span>}
						</span>
					</li>
				);
			})}
		</ol>
	);
}

function StepOne({ onCreated }: { onCreated: (domain: DomainDetailDto) => void }) {
	const queryClient = useQueryClient();
	const [value, setValue] = useState("");
	const [touched, setTouched] = useState(false);
	const [email, setEmail] = useState(true);
	const [sites, setSites] = useState(false);
	const [includeWww, setIncludeWww] = useState(true);
	const [subPurpose, setSubPurpose] = useState<"email" | "sites" | "hub" | "webmail">("email");

	const result = validateDomain(value);
	const apex = result.ok && isApexHostname(result.normalized);
	const clientError = touched && value.trim().length > 0 && !result.ok ? (result.message ?? null) : null;
	const normalisedDiffers = result.ok && result.normalized !== value.trim().toLowerCase();
	const purposeError =
		touched && result.ok && apex && !email && !sites ? "Choose email, a website, or both." : null;

	const create = useMutation({
		mutationFn: () => {
			if (apex) {
				return api.createDomain(result.normalized, { email, sites, includeWww: sites ? includeWww : false });
			}
			if (subPurpose === "hub" || subPurpose === "webmail") {
				return api.createDomain(result.normalized, { platform: subPurpose });
			}
			return api.createDomain(result.normalized, {
				email: subPurpose === "email",
				sites: subPurpose === "sites",
			});
		},
		onSuccess: (detail) => {
			queryClient.setQueryData(queryKeys.domain(detail.id), detail);
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
			onCreated(detail);
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setTouched(true);
		if (!result.ok) return;
		if (apex && !email && !sites) return;
		create.mutate();
	}

	return (
		<Section
			title="Which hostname are you adding?"
			description="Apex or subdomain. For example jages.com or hub.jages.com. Key is not a registrar, so nothing here changes who the name is registered with."
		>
			<form className="flex max-w-lg flex-col gap-4" onSubmit={submit} noValidate>
				<Field
					label="Domain name"
					help="For example yourcompany.com or hub.yourcompany.com. Leave out https."
					error={clientError}
					required
				>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={value}
							required
							autoFocus
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							placeholder="yourcompany.com"
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setValue(event.target.value)}
							onBlur={() => setTouched(true)}
						/>
					)}
				</Field>

				{normalisedDiffers && (
					<InlineNotice tone="info">
						We will add <span className="text-dns">{result.normalized}</span>.
					</InlineNotice>
				)}

				{result.ok && apex && (
					<fieldset className="flex flex-col gap-3">
						<legend className="text-sm font-medium text-(--text-primary)">What is this domain for?</legend>
						<div className="mt-4 flex flex-col gap-3">
							<Checkbox
								checked={email}
								label="Email"
								description="Send and receive mail on this domain. Uses MX, SPF and DKIM."
								onChange={(event) => setEmail(event.target.checked)}
							/>
							<Checkbox
								checked={sites}
								label="Website"
								description="Serve a site on this name. The root uses ALIAS or A, not a normal CNAME."
								onChange={(event) => setSites(event.target.checked)}
							/>
							{sites && (
								<Checkbox
									checked={includeWww}
									label={`Also connect www.${result.normalized}`}
									description="Adds a CNAME for www. You can turn this off if you only want the root."
									onChange={(event) => setIncludeWww(event.target.checked)}
								/>
							)}
						</div>
						<p className="text-xs text-(--text-muted)">
							Hub and Webmail use their own hostnames, such as hub.{result.normalized} and webmail.
							{result.normalized}.
						</p>
						{purposeError && <p className="text-sm text-(--color-critical-700)">{purposeError}</p>}
					</fieldset>
				)}

				{result.ok && !apex && (
					<fieldset className="flex flex-col gap-3">
						<legend className="text-sm font-medium text-(--text-primary)">What is this hostname for?</legend>
						<div className="mt-4 flex flex-col gap-3">
						<label className="flex items-start gap-2.5 text-sm">
							<input
								type="radio"
								name="purpose"
								checked={subPurpose === "email"}
								onChange={() => setSubPurpose("email")}
								className="mt-0.5"
							/>
							<span>
								<span className="font-medium text-(--text-primary)">Email</span>
								<span className="block text-xs text-(--text-muted)">MX, SPF and DKIM on this hostname.</span>
							</span>
						</label>
						<label className="flex items-start gap-2.5 text-sm">
							<input
								type="radio"
								name="purpose"
								checked={subPurpose === "sites"}
								onChange={() => setSubPurpose("sites")}
								className="mt-0.5"
							/>
							<span>
								<span className="font-medium text-(--text-primary)">Website</span>
								<span className="block text-xs text-(--text-muted)">One CNAME to Key's site origin.</span>
							</span>
						</label>
						<label className="flex items-start gap-2.5 text-sm">
							<input
								type="radio"
								name="purpose"
								checked={subPurpose === "hub"}
								onChange={() => setSubPurpose("hub")}
								className="mt-0.5"
							/>
							<span>
								<span className="font-medium text-(--text-primary)">Hub</span>
								<span className="block text-xs text-(--text-muted)">CNAME this hostname to Hub.</span>
							</span>
						</label>
						<label className="flex items-start gap-2.5 text-sm">
							<input
								type="radio"
								name="purpose"
								checked={subPurpose === "webmail"}
								onChange={() => setSubPurpose("webmail")}
								className="mt-0.5"
							/>
							<span>
								<span className="font-medium text-(--text-primary)">Webmail</span>
								<span className="block text-xs text-(--text-muted)">CNAME this hostname to Webmail.</span>
							</span>
						</label>
						</div>
					</fieldset>
				)}

				{create.isError && <ErrorPanel error={create.error} title="We could not add this domain." />}

				<div className="flex flex-wrap gap-2">
					<Button
						type="submit"
						variant="primary"
						pending={create.isPending}
						pendingLabel="Adding your domain…"
						iconRight={<ArrowRightIcon className="size-4" aria-hidden="true" />}
					>
						Continue
					</Button>
					<LinkButton to="/manage/domains" variant="ghost">
						Cancel
					</LinkButton>
				</div>
			</form>
		</Section>
	);
}

function useRecheck(domainId: string) {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	return useMutation({
		mutationFn: () => api.checkDomainDns(domainId),
		onSuccess: (detail) => {
			queryClient.setQueryData(queryKeys.domain(detail.id), detail);
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
		},
		onError: () => {
			toast({ tone: "error", message: "We could not check your DNS just now. Try again in a moment." });
		},
	});
}

function StepTwo({ detail }: { detail: DomainDetailDto }) {
	const recheck = useRecheck(detail.id);
	const record = detail.records.find((item) => item.purpose === "ownership");

	return (
		<Section
			title={`Prove you own ${detail.domainName}`}
			description="Add this one record at whoever manages your domain's DNS. It tells Key the domain is yours and nothing else."
		>
			{record ? (
				<Card className="flex flex-col gap-4">
					<dl className="grid gap-3 sm:grid-cols-3">
						<div className="flex flex-col gap-1">
							<dt className="text-xs font-medium text-(--text-muted)">Type</dt>
							<dd className="text-dns text-(--text-primary)">{record.recordType}</dd>
						</div>
						<div className="flex flex-col gap-1">
							<dt className="text-xs font-medium text-(--text-muted)">Host or name</dt>
							<dd>
								<CopyField value={record.host} label="Host" />
							</dd>
						</div>
						<div className="flex flex-col gap-1">
							<dt className="text-xs font-medium text-(--text-muted)">Value</dt>
							<dd>
								<CopyField value={record.value} label="Value" />
							</dd>
						</div>
					</dl>

					<p className="max-w-prose text-sm text-(--text-secondary)">{record.explanation}</p>
				</Card>
			) : (
				<InlineNotice tone="info">
					We are preparing your verification record. This page updates on its own.
				</InlineNotice>
			)}

			<InlineNotice tone="info" title="DNS changes take a few minutes">
				Most providers publish a new record within five minutes, though some take up to an hour. You can
				leave this page — Key keeps checking and emails you when the domain is verified.
			</InlineNotice>

			<div className="flex flex-wrap items-center gap-3">
				<Button
					variant="primary"
					pending={recheck.isPending}
					pendingLabel="Checking your DNS…"
					onClick={() => recheck.mutate()}
				>
					Check again
				</Button>
				<LinkButton to="/manage/domains" variant="ghost">
					Continue later
				</LinkButton>
				<p className="text-xs text-(--text-muted)" role="status">
					Last checked {formatRelative(detail.lastDnsCheckAt)}
				</p>
			</div>

			{record && record.status === "incorrect" && (
				<InlineNotice tone="caution" title="We found a different value">
					{record.message}
				</InlineNotice>
			)}
		</Section>
	);
}

function StepThree({ detail }: { detail: DomainDetailDto }) {
	const recheck = useRecheck(detail.id);
	const mailRecords = detail.records.filter((record) =>
		["mx", "spf", "dkim", "dmarc", "autoconfig", "autodiscover"].includes(record.purpose),
	);
	const siteRecords = detail.records.filter((record) => record.purpose === "site" || record.purpose === "cname");
	const platformRecords = detail.records.filter((record) => record.purpose === "platform");
	const mxConflicts = detail.conflicts.filter((conflict) => conflict.purpose === "mx");
	const otherConflicts = detail.conflicts.filter((conflict) => conflict.purpose !== "mx");
	const title =
		detail.platformApp === "hub" ? "Point this hostname at Hub"
		: detail.platformApp === "webmail" ? "Point this hostname at Webmail"
		: detail.emailEnabled && detail.sitesEnabled ? "Connect email and the website"
		: detail.sitesEnabled ? "Point this hostname at the website"
		: "Turn on email for this domain";

	return (
		<Section
			title={title}
			description="Add these records at your DNS provider. Key checks them automatically and shows what is still missing."
		>
			<InlineNotice tone="positive" title="Ownership confirmed">
				{detail.domainName} is verified. Now point its mail to Key.
			</InlineNotice>

			{mxConflicts.length > 0 && (
				<InlineNotice tone="critical" title="Another email provider still receives this domain's mail">
					<div className="flex flex-col gap-1">
						{mxConflicts.map((conflict) => (
							<p key={conflict.message}>{conflict.message}</p>
						))}
						<p>
							Remove the old MX records once you are ready to switch. Key will not delete DNS records
							you did not ask it to.
						</p>
					</div>
				</InlineNotice>
			)}

			{otherConflicts.map((conflict) => (
				<InlineNotice key={conflict.message} tone="caution" title="One record needs attention">
					{conflict.message}
				</InlineNotice>
			))}

			{detail.emailEnabled && mailRecords.length > 0 && (
				<div className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-(--text-primary)">Email</h3>
					<DnsRecordList records={mailRecords} />
				</div>
			)}

			{detail.sitesEnabled && siteRecords.length > 0 && (
				<div className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-(--text-primary)">Website</h3>
					<InlineNotice tone="info" title="Do not use a normal CNAME at the root">
						If this is the apex, use an ALIAS, ANAME, or flattened CNAME. A standard CNAME at the root
						cannot sit next to MX records and will stop email.
					</InlineNotice>
					<DnsRecordList records={siteRecords} />
				</div>
			)}

			{platformRecords.length > 0 && <DnsRecordList records={platformRecords} />}

			<div className="flex flex-wrap items-center gap-3">
				<Button
					variant="primary"
					pending={recheck.isPending}
					pendingLabel="Checking your DNS…"
					onClick={() => recheck.mutate()}
				>
					Check again
				</Button>
				<LinkButton to="/manage/domains" variant="ghost">
					Continue later
				</LinkButton>
				<p className="text-xs text-(--text-muted)" role="status">
					Last checked {formatRelative(detail.lastDnsCheckAt)}
				</p>
			</div>

			<CloudflareConnect detail={detail} />
		</Section>
	);
}

function CloudflareConnect({ detail }: { detail: DomainDetailDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [approved, setApproved] = useState<Set<string>>(new Set());
	const [confirmed, setConfirmed] = useState(false);

	const preview = useMutation({
		mutationFn: () => api.previewCloudflareRecords(detail.id),
		onSuccess: (result) => {
			setApproved(new Set(result.records.map((record) => record.id)));
			setConfirmed(false);
		},
	});

	const apply = useMutation({
		mutationFn: () => {
			const zoneId = preview.data?.zoneId;
			if (!zoneId) throw new Error("Preview the changes first.");
			// Echoing back the previewed zone means a customer can only ever
			// approve the zone they were actually shown.
			return api.applyCloudflareRecords(detail.id, {
				zoneId,
				approvedRecordIds: [...approved],
			});
		},
		onSuccess: (updated) => {
			queryClient.setQueryData(queryKeys.domain(updated.id), updated);
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
			preview.reset();
			toast({ tone: "success", message: "Key added the records to Cloudflare." });
		},
	});

	return (
		<Card className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<h3 className="flex items-center gap-2 text-sm font-semibold text-(--text-primary)">
					<CloudIcon className="size-4" aria-hidden="true" />
					Using Cloudflare for DNS?
				</h3>
				<p className="max-w-prose text-sm text-(--text-secondary)">
					Key can add these records for you. You will see exactly what it will add before anything
					changes, and it never removes records you already have.
				</p>
			</div>

			<InlineNotice tone="info" title="Key never asks for a Global API key">
				Authorisation is limited to editing DNS for this one zone. A Global API key would give access to your
				whole Cloudflare account, so Key does not accept one.
			</InlineNotice>

			{preview.isError && (
				<ErrorPanel error={preview.error} title="We could not read your Cloudflare zone." />
			)}

			{preview.data ? (
				<div className="flex flex-col gap-4">
					<p className="text-sm text-(--text-secondary)">
						Zone <span className="text-dns">{preview.data.zoneName}</span>. Choose what Key should add:
					</p>

					<ul className="flex flex-col gap-2">
						{preview.data.records.map((record) => (
							<li key={record.id} className="rounded-(--radius-md) bg-(--surface-sunken) p-3">
								<Checkbox
									checked={approved.has(record.id)}
									label={record.summary}
									description={record.conflict ?? undefined}
									onChange={(event) => {
										setApproved((current) => {
											const next = new Set(current);
											if (event.target.checked) next.add(record.id);
											else next.delete(record.id);
											return next;
										});
									}}
								/>
								{record.conflict && (
									<p className="mt-2 flex items-start gap-1.5 text-xs text-(--color-caution-700)">
										<WarningCircleIcon
											className="mt-px size-3.5 shrink-0"
											weight="fill"
											aria-hidden="true"
										/>
										This replaces nothing. Review the existing record before you continue.
									</p>
								)}
							</li>
						))}
					</ul>

					<Checkbox
						checked={confirmed}
						label={`Add these ${approved.size === 1 ? "record" : "records"} to ${preview.data.zoneName}`}
						description="Nothing is written to Cloudflare until you tick this and confirm."
						onChange={(event) => setConfirmed(event.target.checked)}
					/>

					{apply.isError && (
						<ErrorPanel error={apply.error} title="We could not add the records to Cloudflare." />
					)}

					<div className="flex flex-wrap gap-2">
						<Button
							variant="primary"
							disabled={!confirmed || approved.size === 0}
							pending={apply.isPending}
							pendingLabel="Adding records…"
							onClick={() => apply.mutate()}
						>
							Add records
						</Button>
						<Button variant="ghost" onClick={() => preview.reset()} disabled={apply.isPending}>
							Cancel
						</Button>
					</div>
				</div>
			) : (
				<div>
					<Button
						variant="secondary"
						pending={preview.isPending}
						pendingLabel="Reading your zone…"
						onClick={() => preview.mutate()}
					>
						Connect Cloudflare
					</Button>
				</div>
			)}
		</Card>
	);
}

function ReadyState({ detail }: { detail: DomainDetailDto }) {
	const lines = [
		detail.emailEnabled ? "Email is ready." : null,
		detail.sitesEnabled ? "The website hostname is ready." : null,
		detail.platformApp === "hub" ? "Hub is ready on this hostname." : null,
		detail.platformApp === "webmail" ? "Webmail is ready on this hostname." : null,
	].filter(Boolean);

	return (
		<Section>
			<Card className="flex flex-col items-start gap-4">
				<CheckCircleIcon
					className="size-8 text-(--color-positive-600)"
					weight="fill"
					aria-hidden="true"
				/>
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-(--text-primary)">{detail.domainName} is ready.</h2>
					<p className="max-w-prose text-sm text-(--text-secondary)">{lines.join(" ")}</p>
				</div>

				<div className="flex flex-wrap gap-2">
					{detail.emailEnabled && (
						<LinkButton
							to={`/manage/email/mailboxes/new?domain=${detail.id}`}
							variant="primary"
							iconRight={<ArrowRightIcon className="size-4" aria-hidden="true" />}
						>
							Create your first mailbox
						</LinkButton>
					)}
					{detail.sitesEnabled && (
						<LinkButton to="/site/sites" variant={detail.emailEnabled ? "secondary" : "primary"}>
							Open Sites
						</LinkButton>
					)}
					<LinkButton to={`/manage/domains/${detail.id}`} variant="secondary">
						View domain
					</LinkButton>
				</div>
			</Card>
		</Section>
	);
}
